
import React from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Story, MOCK_AUTHORS, MOCK_STORIES } from '@/utils/storiesData';
import { storyFormSchema, StoryFormValues } from '@/pages/Admin/StoryEditorSchema';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Save, Image } from 'lucide-react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface StoryFormProps {
  existingStory: Story | null;
  onSubmit: (data: StoryFormValues) => void;
  onPreview: (formValues: StoryFormValues) => void;
}

const StoryForm: React.FC<StoryFormProps> = ({ existingStory, onSubmit, onPreview }) => {
  const categories = Array.from(new Set(MOCK_STORIES.map(story => story.category)));
  
  const form = useForm<StoryFormValues>({
    resolver: zodResolver(storyFormSchema),
    defaultValues: existingStory 
      ? {
          title: existingStory.title,
          subtitle: existingStory.subtitle,
          excerpt: existingStory.excerpt,
          content: existingStory.content,
          coverImage: existingStory.coverImage,
          category: existingStory.category,
          authorId: existingStory.author.id,
          tags: existingStory.tags.join(", "),
          featured: existingStory.featured,
        }
      : {
          title: "",
          subtitle: "",
          excerpt: "",
          content: "",
          coverImage: "https://images.unsplash.com/photo-1542435503-956c469947f6?q=80&w=1200",
          category: "",
          authorId: "",
          tags: "",
          featured: false,
        }
  });

  const handleSubmit = (data: StoryFormValues) => {
    onSubmit(data);
  };

  const triggerPreview = () => {
    const formValues = form.getValues();
    onPreview(formValues);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="bg-card rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-gt-super">Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter story title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="subtitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subtitle</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter story subtitle" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="excerpt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Excerpt</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Brief summary of the story" 
                        className="min-h-[80px]" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      This will appear in story previews and cards.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map(category => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                        <SelectItem value="New Category">Add New Category</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <FormField
                control={form.control}
                name="authorId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an author" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {MOCK_AUTHORS.map(author => (
                          <SelectItem key={author.id} value={author.id}>
                            {author.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="coverImage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cover Image</FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <FormControl>
                          <Input placeholder="Image URL" {...field} />
                        </FormControl>
                      </div>
                      <div className="flex items-center justify-center bg-muted rounded-md overflow-hidden h-[100px]">
                        {field.value ? (
                          <img 
                            src={field.value} 
                            alt="Cover preview" 
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = "https://via.placeholder.com/300x200?text=Invalid+Image+URL";
                            }}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <Image className="h-8 w-8 mb-2" />
                            <span className="text-xs">No image</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tags, separated by commas" {...field} />
                    </FormControl>
                    <FormDescription>
                      Example: design, architecture, minimalism
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Story content (supports Markdown)" 
                        className="min-h-[300px]" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Write your story content using Markdown for formatting.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={triggerPreview}>
            Preview
          </Button>
          <Button type="submit">
            <Save className="h-4 w-4 mr-2" />
            {existingStory ? 'Update Story' : 'Create Story'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default StoryForm;
